import type {
  MarketPrice,
  PutAdminV1StoresStoreIdMarketsMarketIdPricesBodyItemsItem,
} from '@nucleus-commerce/api-client/admin';
import type { Money } from '@nucleus-commerce/core';

/** A variant's fixed price in one market while it is edited; null price means none is set. */
export interface MarketPriceDraft {
  price: Money | null;
  compareAt: Money | null;
}

export function draftFromEntry(entry: MarketPrice | undefined): MarketPriceDraft {
  return { price: entry?.price ?? null, compareAt: entry?.compareAt ?? null };
}

const sameAmount = (a: Money | null, b: Money | null) =>
  (a?.amount ?? null) === (b?.amount ?? null);

/**
 * True when saving would write a fixed price. Clearing the price of a saved row is not a save:
 * the PUT only upserts, so removing goes through the row's own Remove action.
 */
export function isMarketDraftDirty(
  draft: MarketPriceDraft,
  entry: MarketPrice | undefined,
): boolean {
  if (!draft.price) return false;
  const saved = draftFromEntry(entry);
  return !sameAmount(draft.price, saved.price) || !sameAmount(draft.compareAt, saved.compareAt);
}

/** PUT items for the changed rows; untouched rows keep their entries on the server. */
export function toMarketPriceItems(
  drafts: ReadonlyMap<string, MarketPriceDraft>,
  entries: ReadonlyMap<string, MarketPrice>,
): PutAdminV1StoresStoreIdMarketsMarketIdPricesBodyItemsItem[] {
  return [...drafts].flatMap(([variantId, draft]) =>
    draft.price && isMarketDraftDirty(draft, entries.get(variantId))
      ? [{ variantId, price: draft.price, compareAt: draft.compareAt }]
      : [],
  );
}

/** The variant a refused PUT points at (`body.items.3.price.currency`), from the items it sent. */
export function refusedVariantId(
  field: string | undefined,
  items: readonly PutAdminV1StoresStoreIdMarketsMarketIdPricesBodyItemsItem[],
): string | null {
  const index = /^body\.items\.(\d+)\./.exec(field ?? '')?.[1];
  return index === undefined ? null : (items[Number(index)]?.variantId ?? null);
}
